import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../styles/shared';

// ─────────────────────────────────────────────────────────────────────────────
// useAnalysis
//
// FIX (S27E-hotfix2):
//   - 401 response: fires global 'logout' event → useAuth clears token/user state
//     → App shows sign-in modal with "Session expired" message.
//     User is never stuck on a broken state.
//   - 403 (unverified): shows verification message.
//   - 429 (limit reached): shows upgrade message.
//   - All other errors: show message from server or generic fallback.
// ─────────────────────────────────────────────────────────────────────────────
export function useAnalysis(token, setUser) {
  const [analysis, setAnalysis] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const handleAuthError = useCallback((status, message) => {
    if (status === 401) {
      // Token is invalid or expired — clear auth state globally
      // useAuth listens for this event and calls logout()
      window.dispatchEvent(new Event('logout'));
      setError('SESSION_EXPIRED:Your session has expired. Please sign in again.');
      return;
    }
    if (status === 403) {
      setError('UNVERIFIED:' + (message || 'Please verify your email first'));
      return;
    }
    if (status === 429) {
      setError('LIMIT_REACHED:' + (message || 'Monthly analysis limit reached'));
      return;
    }
    setError(message || 'Analysis failed — please try again');
  }, []);

  const analyze = useCallback(async (ticker) => {
    if (!ticker?.trim()) { setError('Please enter a ticker symbol'); return false; }
    setError(''); setLoading(true); setAnalysis(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/stocks/analyze`,
        { ticker: ticker.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setAnalysis(res.data.data);
        if (res.data.data.analysesRemaining !== undefined && setUser) {
          setUser(prev => prev
            ? { ...prev, analysesThisMonth: (prev.analysesLimit || 5) - res.data.data.analysesRemaining }
            : prev
          );
        }
        return res.data.data;
      }
      setError(res.data.message || 'Analysis failed');
      return false;
    } catch (err) {
      handleAuthError(err.response?.status, err.response?.data?.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token, setUser, handleAuthError]);

  const analyzeGuest = useCallback(async (ticker) => {
    if (!ticker?.trim()) { setError('Please enter a ticker symbol'); return false; }
    setError(''); setLoading(true); setAnalysis(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/stocks/analyze-guest`,
        { ticker: ticker.trim().toUpperCase() }
      );
      if (res.data.success) {
        setAnalysis({ ...res.data.data, isGuest: true });
        return true;
      }
      setError(res.data.message || 'Analysis failed');
      return false;
    } catch (err) {
      setError(err.response?.data?.message || 'Analysis failed');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearAnalysis = useCallback(() => {
    setAnalysis(null);
    setError('');
  }, []);

  return { analysis, loading, error, setError, analyze, analyzeGuest, clearAnalysis };
}
